import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import Spinner from "../../../../../Components/common/Spinner/Spinner";
import Warning from "../../../../../Components/common/Warning/Warning";

import ArrowBottomIcon from "../../../../../Components/Icon-SVG/ArrowBottomIcon";
import ArrowTopIcon from "../../../../../Components/Icon-SVG/ArrowTopIcon";
import TrashBucketIcon from "../../../../../Components/Icon-SVG/TrashBucketIcon";
import LockIcon from "../../../../../Components/Icon-SVG/LockIcon";
import DynamicIcon from "../../../../../Components/Icon-SVG/DynamicIcon";
import SearchIcon from "../../../../../Components/Icon-SVG/SearchIcon";
import AgentIcon from "../../../../../Components/Icon-SVG/AgentIcon";

import axiosApi from "../../../../../utils/axios";
import useFetchData from "../../../../../utils/hook/useFetchData";

import { RequestDeleteAgentGroup } from "../../../../../store/actions/integrationsActions";

const ExpandButton = ({ isExpanded }) => {
  return isExpanded ? (
    <ArrowTopIcon color="white" width={12} height={12} />
  ) : (
    <ArrowBottomIcon color="white" width={12} height={12} />
  );
};

const AgentList = ({
  agentList = [],
  loading = false,
  listType = "available", // listType is one of "available", "member", "matched"
  onAddToGroup = () => {},
  onRemoveFromGroup = () => {},
}) => {
  return (
    <div className={`agent-group__list-item__${listType}-agent-list`}>
      {loading ? (
        <div className="spinner-container">
          <Spinner width={20} height={20} />
        </div>
      ) : (
        agentList.map((agent) => (
          <div
            key={agent._id.$oid}
            className={`agent-group__list-item__${listType}-agent-list__item`}>
            <AgentIcon width={16} height={16} />
            <span>{agent.client_id || "-"}</span>
            <span>
              {agent.hostName} - {agent.macAddress} - {agent.cpuID}
            </span>
            {listType !== "matched" ? (
              <div className="action">
                {listType === "available" ? (
                  <span onClick={() => onAddToGroup(agent._id.$oid)}>+</span>
                ) : (
                  <span onClick={() => onRemoveFromGroup(agent._id.$oid)}>x</span>
                )}
              </div>
            ) : null}
          </div>
        ))
      )}
    </div>
  );
};

const StaticGroupDetail = ({ selectedHost, groupId, setMemberCount }) => {
  const {
    data: availableAgents,
    loading: availableAgentsLoading,
    refetch: refetchAvailable,
  } = useFetchData(`/api/agent/groups/${groupId}/${selectedHost}/available`);

  const {
    data: memberAgents,
    loading: memberAgentsLoading,
    refetch: refetchMembers,
  } = useFetchData(`/api/agent/groups/${groupId}/${selectedHost}/members`);

  const [filterAvailableAgents, setFilterAvailableAgents] = useState("");
  const [filterMemberAgents, setFilterMemberAgents] = useState("");

  const filteredAvailableAgents = availableAgents.filter(
    (agent) =>
      agent.client_id.toLowerCase().includes(filterAvailableAgents.toLowerCase()) ||
      agent.cpuID.toLowerCase().includes(filterAvailableAgents.toLowerCase()) ||
      agent.hostName.toLowerCase().includes(filterAvailableAgents.toLowerCase()) ||
      agent.macAddress.toLowerCase().includes(filterAvailableAgents.toLowerCase()),
  );
  const filteredMemberAgents = memberAgents.filter(
    (agent) =>
      agent.client_id.toLowerCase().includes(filterMemberAgents.toLowerCase()) ||
      agent.cpuID.toLowerCase().includes(filterMemberAgents.toLowerCase()) ||
      agent.hostName.toLowerCase().includes(filterMemberAgents.toLowerCase()) ||
      agent.macAddress.toLowerCase().includes(filterMemberAgents.toLowerCase()),
  );

  const addToGroupHandler = async (agentId) => {
    try {
      await axiosApi.post(`/api/agent/groups/${groupId}/agents/${agentId}/ag-cagapp-cf-sb1-agent`);
      await Promise.all([refetchAvailable(), refetchMembers()]);
      setMemberCount((prevState) => prevState + 1);
    } catch (e) {
      console.log(e);
    }
  };

  const removeFromGroupHandler = async (agentId) => {
    try {
      await axiosApi.delete(
        `/api/agent/groups/${groupId}/agents/${agentId}/ag-cagapp-cf-sb1-agent`,
      );
      await Promise.all([refetchAvailable(), refetchMembers()]);
      setMemberCount((prevState) => prevState - 1);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className="agent-group__list-item__detail">
      <div className="agent-group__list-item__available-agents">
        <h4>AVAILABLE HOSTS ({filteredAvailableAgents.length})</h4>
        <div className="agent-group__list-item__input-container">
          <SearchIcon color="rgb(155, 155, 155)" width={12} height={12} />
          <input
            placeholder="Search client id, hostname, mac address or cpu id..."
            onChange={(e) => setFilterAvailableAgents(e.target.value)}
          />
        </div>
        <AgentList
          agentList={filteredAvailableAgents}
          loading={availableAgentsLoading}
          listType="available"
          onAddToGroup={addToGroupHandler}
        />
      </div>
      <div className="agent-group__list-item__group-members">
        <h4>GROUP MEMBERS ({filteredMemberAgents.length})</h4>
        <div className="agent-group__list-item__input-container">
          <SearchIcon color="rgb(155, 155, 155)" width={12} height={12} />
          <input
            placeholder="Search client id, hostname, mac address or cpu id..."
            onChange={(e) => setFilterMemberAgents(e.target.value)}
          />
        </div>
        <AgentList
          agentList={filteredMemberAgents}
          loading={memberAgentsLoading}
          listType="member"
          onRemoveFromGroup={removeFromGroupHandler}
        />
      </div>
    </div>
  );
};

const DynamicGroupDetail = ({ selectedHost, groupId, pattern }) => {
  const { data, loading } = useFetchData(`/api/agent/groups/${groupId}/${selectedHost}/matched`);

  return (
    <div className="agent-group__list-item__detail">
      <div className="agent-group__list-item__matched-agents">
        <div>
          <p>
            Pattern: <span>{pattern}</span>
          </p>
        </div>
        <h4>MATCHED HOSTS ({data.length})</h4>
        <AgentList agentList={data} loading={loading} listType="matched" />
      </div>
    </div>
  );
};

const AgentGroupListItem = ({ groupItem, selectedHost }) => {
  const dispatch = useDispatch();

  const [isExpanded, setIsExpanded] = useState(false);
  const [memberCount, setMemberCount] = useState(groupItem.member_count);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    setMemberCount(groupItem.member_count);
  }, [groupItem.member_count]);

  const warningClickOKHandler = () => {
    dispatch(RequestDeleteAgentGroup({ id: groupItem.id, host: selectedHost }));
    setShowWarning(false);
  };

  return (
    <div className="agent-group__list-item border">
      <div className="agent-group__list-item__top">
        <div className="agent-group__list-item__left">
          <div className="agent-group__list-item__group-icon">
            {groupItem.type === "static" ? (
              <LockIcon color="var(--datepicker-date-bg)" />
            ) : (
              <DynamicIcon color="#58e655" />
            )}
          </div>
          <div className="agent-group__list-item__info">
            <h3>{groupItem.name}</h3>
            <p>{groupItem.description}</p>
          </div>
        </div>
        <div className="agent-group__list-item__right">
          <span className={`type --${groupItem.type}`}>{groupItem.type}</span>
          <span>
            {memberCount} agent{memberCount < 2 ? "" : "s"}
          </span>
          <button onClick={() => setIsExpanded((prevState) => !prevState)}>
            <ExpandButton isExpanded={isExpanded} />
          </button>
          <button onClick={() => setShowWarning(true)}>
            <TrashBucketIcon />
          </button>
        </div>
      </div>
      {isExpanded ? (
        groupItem.type === "static" ? (
          <StaticGroupDetail
            selectedHost={selectedHost}
            groupId={groupItem.id}
            setMemberCount={setMemberCount}
          />
        ) : (
          <DynamicGroupDetail
            selectedHost={selectedHost}
            groupId={groupItem.id}
            pattern={groupItem.client_id_pattern}
          />
        )
      ) : null}
      <Warning
        onClickOk={warningClickOKHandler}
        show={showWarning}
        setShow={setShowWarning}
        text="Are you sure you want to delete the agent group ?"
        article="Confirm delete"
        cancelDisable={false}
      />
    </div>
  );
};

export default AgentGroupListItem;
